import {
  DEFAULT_LINK_CLASSES,
  FOCUS_RING,
  LINK_BLUE_CLASSES,
  LINK_GRAY_CLASSES,
  LINK_UNDERLINE_CLASSES,
  LINK_WHITE_CLASSES,
} from "@packages/design-system/constants";
import { cn } from "@packages/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const buttonVariants = cva(
  cn(
    "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap tracking-wide ease-in hover:transition-colors hover:duration-200 disabled:pointer-events-none disabled:opacity-50",
    FOCUS_RING,
  ),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:text-primary-foreground group-hover:text-primary-foreground shadow-sm hover:bg-primary/90 group-hover:bg-primary/90 font-semibold active:translate-y-px",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 font-semibold active:translate-y-px",
        destructiveOutline:
          "border-destructive border text-destructive shadow-xs hover:text-destructive-foreground hover:bg-destructive font-semibold active:translate-y-px",
        outline:
          "bg-background border border-border text-heading hover:text-heading/80 shadow-xs hover:bg-secondary/80 font-semibold active:translate-y-px",
        outlineStrong:
          "bg-secondary-light/80 border border-border text-heading hover:text-heading/80 shadow-xs hover:bg-secondary font-semibold active:translate-y-px",
        softBlue:
          "bg-button-soft text-button-soft-foreground border border-button-soft-hover shadow-xs hover:bg-button-soft-hover font-semibold active:translate-y-px",
        success:
          "bg-success text-primary-foreground hover:text-primary-foreground shadow-xs hover:bg-success/90 font-semibold active:translate-y-px",
        ghost:
          "hover:bg-accent hover:text-accent-foreground active:translate-y-px",
        link: LINK_BLUE_CLASSES + " " + DEFAULT_LINK_CLASSES,
        grayLink: LINK_GRAY_CLASSES + " " + DEFAULT_LINK_CLASSES,
        whiteLink: LINK_WHITE_CLASSES + " " + DEFAULT_LINK_CLASSES,
        underlineLink:
          LINK_BLUE_CLASSES +
          " " +
          LINK_UNDERLINE_CLASSES +
          " " +
          DEFAULT_LINK_CLASSES,
        disabled:
          "text-muted bg-muted-foreground pointer-events-none font-semibold",
        adminSelected:
          "bg-accent text-link-hover hover:cursor-default font-semibold active:translate-y-px",
        noStyles: "",
      },
      size: {
        default: "h-9 px-2 py-1 text-xs w-full md:w-auto rounded-md",
        sm: "h-8 px-2 py-1 text-sm w-full md:w-auto rounded-md",
        md: "px-2.5 py-1.5 text-sm w-full md:w-auto rounded-md",
        lg: "px-4 py-2 text-sm w-full md:w-auto rounded-md",
        xl: "px-6 py-2.5 text-base w-full md:w-auto rounded-md",
        none: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const additionalClasses =
      variant !== "noStyles" && variant !== "ghost"
        ? "text-sm rounded-md [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
        : "";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          additionalClasses,
        )}
        ref={ref}
        {...props}
      >
        <span className="inline-flex max-w-full items-center gap-x-2 truncate">
          {children}
        </span>
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
